import {
    Box,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

import { bcRow, bcTableFrame, bcTd, bcTh } from "@/routes/table-styles";

import type { WidgetProps } from "./types";

// Markdown-rendering body widget. Pattern-establishing — every other widget
// follows the same shape: typed WidgetProps<T>, MUI for layout/typography,
// no Next.js imports.
// GFM runs a table until a blank line, so a paragraph written on the line straight after a
// table renders as a one-cell row (Tvilum, Bill, 2026-09-30). Put a blank line after every
// table block, and before it, so stored markdown can never produce that row.
function spaceTables(markdown: string): string {
    const out: string[] = [];
    const isRow = (l: string | undefined) => l !== undefined && l.startsWith("|");
    for (const line of markdown.split("\n")) {
        const prev = out[out.length - 1];
        if (isRow(line) && prev !== undefined && prev.trim() && !isRow(prev)) out.push("");
        if (!isRow(line) && line.trim() && isRow(prev)) out.push("");
        out.push(line);
    }
    return out.join("\n");
}

// GitHub-flavoured tables (2026-09-24, Tvilum room) on the design system's
// .bc-table: navy header, gray rules, gray-50 hover; a right-aligned markdown
// column (`---:`) stays right-aligned. Shared with the checklist's requirements
// popup (MAP.tsx).
export const gfmTableComponents: Components = {
    table: ({ children }) => (
        // Sized to its content: a four-column summary stays narrow,
        // a nine-column grid runs to the page width (Bill, 2026-09-24:
        // a full-width table with three columns reads badly).
        <TableContainer
            sx={{
                ...bcTableFrame,
                overflowX: "auto",
                mb: 2,
                width: "auto",
                maxWidth: "100%",
                display: "inline-block",
            }}
        >
            <Table size="small" sx={{ width: "auto", maxWidth: "100%" }}>
                {children}
            </Table>
        </TableContainer>
    ),
    thead: ({ children }) => <TableHead>{children}</TableHead>,
    tbody: ({ children }) => <TableBody>{children}</TableBody>,
    tr: ({ children }) => <TableRow sx={bcRow}>{children}</TableRow>,
    th: ({ children, style }) => (
        // Header labels wrap on a phone: nowrap pushed a three-column table to
        // 833px inside a 323px column (Tvilum, Bill, 2026-09-30).
        <TableCell
            sx={{
                ...bcTh,
                textAlign: style?.textAlign ?? "left",
                whiteSpace: { xs: "normal", md: "nowrap" },
            }}
        >
            {children}
        </TableCell>
    ),
    td: ({ children, style }) => (
        <TableCell
            sx={{
                ...bcTd,
                textAlign: style?.textAlign ?? "left",
                fontVariantNumeric: "tabular-nums",
            }}
        >
            {children}
        </TableCell>
    ),
};

export function RichText({ widget }: WidgetProps<"rich_text">) {
    const markdown = spaceTables(widget.props.markdown);

    return (
        <Box sx={{ "& > *:first-of-type": { mt: 0 }, "& > *:last-child": { mb: 0 } }}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    ...gfmTableComponents,
                    h1: ({ children }) => (
                        <Typography variant="h2" sx={{ mt: 4, mb: 2 }}>
                            {children}
                        </Typography>
                    ),
                    h2: ({ children }) => (
                        <Typography variant="h3" sx={{ mt: 3.5, mb: 1.5 }}>
                            {children}
                        </Typography>
                    ),
                    h3: ({ children }) => (
                        <Typography variant="h4" sx={{ mt: 3, mb: 1.25 }}>
                            {children}
                        </Typography>
                    ),
                    p: ({ children }) => (
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {children}
                        </Typography>
                    ),
                    a: ({ children, href }) => (
                        <Link href={href} target="_blank" rel="noopener noreferrer">
                            {children}
                        </Link>
                    ),
                    ul: ({ children }) => (
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            {children}
                        </Box>
                    ),
                    ol: ({ children }) => (
                        <Box component="ol" sx={{ pl: 3, mb: 2 }}>
                            {children}
                        </Box>
                    ),
                    li: ({ children }) => (
                        <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
                            {children}
                        </Typography>
                    ),
                    strong: ({ children }) => (
                        <Box component="strong" sx={{ fontWeight: 600, color: "primary.main" }}>
                            {children}
                        </Box>
                    ),
                    em: ({ children }) => <Box component="em">{children}</Box>,
                }}
            >
                {markdown}
            </ReactMarkdown>
        </Box>
    );
}
